import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch, getToken, setToken, API_BASE_URL } from '../dppApi';
import '../App.css';

// Recipient lands here after scanning/following a brand sale link.
// Shows product info, handles login/register inline, then claims the transfer.
const ClaimTransfer = () => {
    const { tokenId } = useParams();
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [claimed, setClaimed] = useState(false);
    const [claimError, setClaimError] = useState(null);
    const [authMode, setAuthMode] = useState('login');
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [busy, setBusy] = useState(false);

    const userToken = getToken('user');
    const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/transfer/token/${tokenId}`)
            .then(r => r.json())
            .then(data => {
                if (data.error) setLoadError(data.error);
                else setInfo(data);
            })
            .catch(() => setLoadError('Failed to load transfer details'));
    }, [tokenId]);

    const doClaim = async (token) => {
        await apiFetch(`/api/transfer/claim/${tokenId}`, { method: 'POST', token });
    };

    const handleClaim = async () => {
        setBusy(true);
        setClaimError(null);
        try {
            await doClaim(userToken);
            setClaimed(true);
        } catch (err) {
            setClaimError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleAuthAndClaim = async (e) => {
        e.preventDefault();
        setBusy(true);
        setClaimError(null);
        try {
            const path = authMode === 'login' ? '/api/user/login' : '/api/user/register';
            const body = authMode === 'login'
                ? { email: form.email, password: form.password }
                : form;
            const data = await apiFetch(path, { method: 'POST', body });
            setToken('user', data.token);
            await doClaim(data.token);
            setClaimed(true);
        } catch (err) {
            setClaimError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (loadError) return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <p className="dpp-error">{loadError}</p>
            </div>
        </div>
    );

    if (!info) return (
        <div className="dpp-auth-page">
            <p className="dpp-muted">Loading…</p>
        </div>
    );

    if (!info.is_valid) return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <p className="dpp-error">This transfer link has already been claimed or has expired.</p>
            </div>
        </div>
    );

    if (claimed) return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <h2 className="dpp-auth-title">Transfer complete</h2>
                <p><strong>{info.product_name}</strong> is now in your collection.</p>
                <button className="dpp-button" onClick={() => navigate('/user/collection')}>View collection</button>
            </div>
        </div>
    );

    return (
        <div className="dpp-auth-page">
            <div className="dpp-auth-card">
                <h2 className="dpp-auth-title">Accept transfer</h2>
                {info.image_url && info.image_url.startsWith('http') && (
                    <img
                        src={info.image_url}
                        alt={info.product_name}
                        style={{ width: '100%', borderRadius: 8, marginBottom: 8, objectFit: 'cover' }}
                    />
                )}
                <p style={{ margin: '4px 0' }}><strong>{info.product_name}</strong></p>
                <p className="dpp-muted" style={{ margin: '0 0 12px' }}>
                    Transfer type: {info.transfer_type}
                </p>

                {claimError && <p className="dpp-error">{claimError}</p>}

                {userToken ? (
                    <button className="dpp-button" onClick={handleClaim} disabled={busy}>
                        {busy ? 'Claiming…' : 'Claim this product'}
                    </button>
                ) : (
                    <form onSubmit={handleAuthAndClaim}>
                        <p className="dpp-muted" style={{ margin: '0 0 8px' }}>
                            {authMode === 'login' ? 'Log in' : 'Sign up'} to claim this product.
                        </p>
                        {authMode === 'register' && (
                            <input className="dpp-input" placeholder="Name" value={form.name}
                                onChange={update('name')} />
                        )}
                        <input className="dpp-input" type="email" placeholder="Email"
                            value={form.email} onChange={update('email')} required />
                        <input className="dpp-input" type="password" placeholder="Password"
                            value={form.password} onChange={update('password')} required />
                        <button className="dpp-button" type="submit" disabled={busy}>
                            {busy ? 'Please wait…' : (authMode === 'login' ? 'Log in & claim' : 'Sign up & claim')}
                        </button>
                        <button type="button" className="dpp-link-button"
                            onClick={() => { setClaimError(null); setAuthMode(authMode === 'login' ? 'register' : 'login'); }}>
                            {authMode === 'login' ? 'Need an account? Sign up' : 'Have an account? Log in'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ClaimTransfer;
